import React, { useState, useCallback, useMemo } from 'react';
import {
  IRapportUtdelning,
  IGrundurval,
} from '../../Queries/AnalysutdelningQuery/AnalysutdelningQuery';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EDButton from '../../../../../../../BaseComponents/Buttons/Button/EDButton';
import { ChangeUtdelningarMutationFnType } from '../../Mutations/ChangeUtdelningar/ChangeUtdelningar';
import { clone, equals, update } from 'ramda';
import AnalysUtdelningAnalysList from '../AnalysUtdelningAnalysList/AnalysUtdelningAnalysList';
import AnalysUtdelningHeader from '../AnalysUtdelningHeader/AnalysUtdelningHeader';
import AnalysUtdelningSideMenu from '../AnalysUtdelningSideMenu/AnalysUtdelningSideMenu';
import {
  findStartAnalys,
  getChangedAnalyser,
  filterAnalyser,
} from './AnalysUtdelningMainUtils';
import EDPaper from '../../../../../../../BaseComponents/Paper/EDPaper';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import DialogScrollOverlay from '../../../../../../../Components/DialogScrollOverlay/DialogScrollOverlay';

interface IProps {
  analyser: IRapportUtdelning[];
  urvalOptions: IGrundurval[];
  changeUtdelningar: ChangeUtdelningarMutationFnType;
  isGasKodplan: boolean;
  activeCodeplanID: string;
}

export type ChangeVisibilityFnType = (
  utdelning: IRapportUtdelning,
  visible: boolean
) => void;
export type SetStartAnalysFnType = (analys: IRapportUtdelning | null) => void;
export type ChangeUrvalFnType = (
  utdelning: IRapportUtdelning,
  urvalId: string
) => void;
export type SetAvailableFnType = (
  analys: IRapportUtdelning,
  available: boolean
) => void;
export type OnClickStartAnalysFnType = () => void;

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      mainPaper: {
        height: `calc(100% - ${theme.spacing(13)})`,
        display: 'flex',
      },
      mainPaperSideMenuOpen: {
        marginLeft: 71, // FIXME: väldigt ostandard siffra
      },
      sideMenuOpenMainWidth: {
        maxWidth: `calc(100% - ${theme.spacing(42)})`,
      },
      sideMenuClosedMainWidth: {
        maxWidth: `calc(100% - ${theme.spacing(13)})`,
      },
      container: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        height: '100%',
        alignItems: 'stretch',
        overflowX: 'auto',
      },
      buttonContainer: {
        backgroundColor: theme.customProperties.Dialog.actionsBg,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        width: '100%',
      },
      rutinContainer: {
        display: 'flex',
        flex: '1 1 auto',
        margin: theme.spacing(2),
        position: 'relative',
      },
    } as const)
);

/**
 * Huvudvyn för analysutdelningsrutinen
 */
const AnalysUtdelningMain: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes, cx } = useStyles();
  const [analyser, setAnalyser] = useState(props.analyser);
  const [originalAnalyser, setOriginalAnalyser] = useState(
    clone(props.analyser)
  );
  const [startAnalys, setStartAnalys] = useState(findStartAnalys(analyser));
  const [searchText, setSearchText] = useState('');
  const [showAvailable, setShowAvailable] = useState(true);
  const [showNotAvailable, setShowNotAvailable] = useState(true);
  const [showVisible, setShowVisible] = useState(true);
  const [showNotVisible, setShowNotVisible] = useState(true);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);

  const setStart: SetStartAnalysFnType = useCallback(updated => {
    if (!updated) {
      return;
    }

    setAnalyser(analyser => {
      const analysIndex = analyser.findIndex(a => a.id === updated.id);
      if (analysIndex >= 0) {
        const oldStartIndex = analyser.findIndex(a => a.startAnalysForNewUser);
        let newAnalyser: IRapportUtdelning[] = analyser;

        // Förra start analysen nollställs
        if (oldStartIndex >= 0) {
          newAnalyser = update(
            oldStartIndex,
            {
              ...analyser[oldStartIndex],
              startAnalysForNewUser: false,
            },
            analyser
          );
        }

        const newAnalys = {
          ...newAnalyser[analysIndex],
          startAnalysForNewUser: true,
          defaultForNewUser: true,
        };

        setStartAnalys(newAnalys);
        return update(analysIndex, newAnalys, newAnalyser);
      } else {
        return analyser;
      }
    });
  }, []);

  const changeVisibility: ChangeVisibilityFnType = useCallback(
    (updated, visible) => {
      if (!updated.startAnalysForNewUser) {
        setAnalyser(analyser => {
          const analysIndex = analyser.findIndex(a => a.id === updated.id);
          return update(
            analysIndex,
            { ...updated, defaultForNewUser: visible },
            analyser
          );
        });
      }
    },
    []
  );

  const changeUrval: ChangeUrvalFnType = useCallback(
    (analys, urvalId) => {
      const urval = props.urvalOptions.find(
        u => u.id === (urvalId === '' ? null : urvalId)
      );

      setAnalyser(analyser => {
        const oldAnalysIndex = analyser.findIndex(a => a.id === analys.id);

        return update(
          oldAnalysIndex,
          { ...analys, grundUrval: urval || null },
          analyser
        );
      });
    },
    [props.urvalOptions]
  );

  const setAvailable: SetAvailableFnType = useCallback((updated, available) => {
    setAnalyser(analyser => {
      const analysIndex = analyser.findIndex(a => a.id === updated.id);
      if (analysIndex >= 0) {
        const newAnalys = { ...analyser[analysIndex] };
        newAnalys.availableForAnalys = available;
        if (!available) {
          newAnalys.defaultForNewUser = false;
        }
        return update(analysIndex, newAnalys, analyser);
      } else {
        return analyser;
      }
    });
  }, []);

  const onClickStartAnalys: OnClickStartAnalysFnType = useCallback(() => {
    const ref = document.querySelector('#start-analys-element');

    if (ref) {
      ref.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, []);

  const save = () => {
    props.changeUtdelningar(getChangedAnalyser(analyser, originalAnalyser));
    setOriginalAnalyser(clone(analyser));
  };

  const clear = () => {
    setAnalyser(clone(originalAnalyser));
    setStartAnalys(findStartAnalys(originalAnalyser));
  };

  const isChanges = equals(originalAnalyser, analyser);

  const filteredAnalyser = useMemo(
    () =>
      filterAnalyser(
        analyser,
        searchText,
        showAvailable,
        showNotAvailable,
        showVisible,
        showNotVisible,
        props.isGasKodplan
      ),
    [
      analyser,
      searchText,
      showAvailable,
      showNotAvailable,
      showVisible,
      showNotVisible,
      props.isGasKodplan,
    ]
  );

  return (
    <>
      <AnalysUtdelningSideMenu
        open={sideMenuOpen}
        setOpen={setSideMenuOpen}
        showAvailable={showAvailable}
        showNotAvailable={showNotAvailable}
        setShowAvailable={setShowAvailable}
        setShowNotAvailable={setShowNotAvailable}
        showVisible={showVisible}
        showNotVisible={showNotVisible}
        setShowVisible={setShowVisible}
        setShowNotVisible={setShowNotVisible}
      />
      <EDPaper
        elevation={2}
        className={cx(classes.rutinContainer, {
          [classes.sideMenuOpenMainWidth]: sideMenuOpen,
          [classes.sideMenuClosedMainWidth]: !sideMenuOpen,
        })}
        square={false}
      >
        <div className={classes.container}>
          <AnalysUtdelningHeader
            activeCodeplanID={props.activeCodeplanID}
            startAnalysName={startAnalys ? startAnalys.name : ''}
            onClickStartAnalys={onClickStartAnalys}
            searchText={searchText}
            onSearchTextChanged={setSearchText}
            isGasKodplan={props.isGasKodplan}
          />
          <AnalysUtdelningAnalysList
            analyser={filteredAnalyser}
            setStartAnalys={setStart}
            changeVisibility={changeVisibility}
            changeUrval={changeUrval}
            urvalOptions={props.urvalOptions}
            setAvailable={setAvailable}
          />
          <DialogScrollOverlay classes={{ container: classes.buttonContainer }}>
            <EDButton
              disabled={isChanges}
              disabledReason="Inga ändringar gjorda"
              onClick={clear}
              label="Ångra"
              flat
              primary
            />
            <EDButton
              marginRight={3}
              marginLeft={2}
              disabled={isChanges}
              disabledReason="Inga ändringar gjorda"
              onClick={save}
              label="Spara"
              primary
              icon={
                <EDIcon
                  iconType="save_alt"
                  color="inherit"
                  size="small"
                  paddingRight={1}
                />
              }
            />
          </DialogScrollOverlay>
        </div>
      </EDPaper>
    </>
  );
};

export default AnalysUtdelningMain;
